import { useParams } from 'react-router-dom';
import { useStateQuery } from '../api/state';
import { useHistoryQuery } from '../api/history';
import { BackLink } from '../components/layout/BackLink';
import { OffsetControl } from '../components/group/OffsetControl';
import { OverrideControl } from '../components/group/OverrideControl';
import { FilterableHistoryChart } from '../components/chart/FilterableHistoryChart';
import { type ChartBand, type ChartPoint } from '../components/chart/HistoryChart';
import { RangeSelector } from '../components/chart/RangeSelector';
import { useChartRange, bucketProps } from '../components/chart/useChartRange';
import { type FloorName, type GroupName, FLOOR_TO_GROUPS } from '../domain';

const FLOOR_LABEL: Record<FloorName, string> = {
  ground: 'Ground Floor',
  middle: 'Middle Floor',
  top: 'Top Floor',
};

function isFloor(x: string | undefined): x is FloorName {
  return x === 'ground' || x === 'middle' || x === 'top';
}

export function FloorDetailPage() {
  const { floor } = useParams<{ floor: string }>();
  const state = useStateQuery();
  const { rangeKey, setRangeKey, range } = useChartRange();
  const history = useHistoryQuery('floor', floor ?? '', range);

  if (!isFloor(floor)) {
    return <div className="banner danger">Unknown floor: {floor}</div>;
  }
  if (state.isLoading) return <div className="skeleton" style={{ height: 200 }} />;
  if (state.isError) return <div className="banner danger">Failed to load state: {(state.error as Error).message}</div>;
  if (!state.data) return null;

  const f = state.data.floors[floor];
  const groups: readonly GroupName[] = FLOOR_TO_GROUPS[floor];

  const rows = history.data?.rows ?? [];
  const readingSeries: ChartPoint[] = rows.map((r) => ({ t: Date.parse(r['at'] as string), v: r['readingC'] as number }));
  const setpointSeries: ChartPoint[] = rows.map((r) => ({ t: Date.parse(r['at'] as string), v: r['setpointEffectiveC'] as number }));
  // Rows arrive NEWEST-first (inverted-ticks RowKey ordering — see
  // contracts/api-rest.md). Band building walks time FORWARD, so iterate an
  // oldest-first copy; otherwise every band is built with from > to and
  // HistoryChart's width clamp renders it as a 1px sliver.
  const chrono = [...rows].reverse();
  const bucketMs = history.data?.bucketMs;
  const bands: ChartBand[] = [];
  if (bucketMs !== undefined) {
    // Aggregated: one band per calling BUCKET, spanning the bucket's width.
    // Contiguous runs would merge unequal duty fractions into one flat block,
    // which is the very dishonesty the fade is there to fix.
    // The newest bucket's band would run one full bucket past the plot's tMax
    // and overhang the chart — negligible at 720 buckets, but a third of the
    // width when the range is sparse. Clamp it to the newest row's timestamp.
    const newestT = chrono.length > 0 ? Date.parse(chrono[chrono.length - 1]!['at'] as string) : 0;
    for (const r of chrono) {
      const call = r['call'];
      if (call !== 'heat' && call !== 'cool') continue;
      const t = Date.parse(r['at'] as string);
      const fraction = r['callFraction'];
      bands.push({
        from: t,
        to: Math.min(t + bucketMs, newestT),
        kind: call === 'cool' ? 'cool' : 'heat',
        intensity: typeof fraction === 'number' && Number.isFinite(fraction) ? fraction : 1,
      });
    }
  } else {
    // Track the run's call kind alongside its start: `FloorCall` includes
    // 'cool', so hardcoding 'heat' would paint an identical cooling period
    // heat-coloured here and cool-coloured on the bucketed path above.
    let bandStart: number | null = null;
    let bandKind: 'heat' | 'cool' = 'heat';
    for (const r of chrono) {
      const t = Date.parse(r['at'] as string);
      const call = r['call'];
      if (call === 'heat' || call === 'cool') {
        if (bandStart === null) { bandStart = t; bandKind = call; }
      } else if (bandStart !== null) {
        bands.push({ from: bandStart, to: t, kind: bandKind });
        bandStart = null;
      }
    }
    if (bandStart !== null && chrono.length > 0) {
      const last = chrono[chrono.length - 1]!;
      bands.push({ from: bandStart, to: Date.parse(last['at'] as string), kind: bandKind });
    }
  }

  return (
    <>
      <BackLink />
      <h1>{FLOOR_LABEL[floor]}</h1>

      <div className="card">
        <div className="reading">{f.reading_c.toFixed(1)} °C</div>
        <div className="muted">
          target {f.setpoint_effective_c.toFixed(1)} °C · <span className={`tag ${f.call}`}>{f.call}</span>
          {f.in_safe_state && ' · SAFE STATE'}
          {f.active_faults.length > 0 && ` · faults: ${f.active_faults.join(', ')}`}
        </div>
      </div>

      <h2>History</h2>
      <div className="card">
        <div className="card-header">
          <RangeSelector value={rangeKey} onChange={setRangeKey} />
          {history.isFetching && <span className="muted" style={{ fontSize: 'var(--fs-sm)' }}>refreshing…</span>}
        </div>
        {history.isLoading
          ? <div className="skeleton" style={{ height: 200 }} />
          : <FilterableHistoryChart width={680} height={220}
                          series={[
                            { data: readingSeries, label: 'reading', filterable: true, average: true },
                            { data: setpointSeries, label: 'setpoint', dashed: true },
                          ]}
                          bands={bands} yLabel="°C"
                          {...bucketProps(history.data)} />}
      </div>

      <h2>Actuator groups</h2>
      {groups.map((g) => {
        const gs = state.data!.groups[g];
        return (
          <div key={g} className="card">
            <div className="card-header">
              <strong>{g}</strong>
              <span className={`tag ${gs.state}`}>{gs.state}</span>
            </div>
            <OverrideControl group={g} active={gs.active_override} />
            <OffsetControl group={g} current={gs.offset_c} />
          </div>
        );
      })}
    </>
  );
}
